import { useState } from "react";
import { Link } from "react-router-dom";

interface DreamEntry {
  id: string;
  title: string;
  date: string;
  mood: string;
  lucidity: number;
  description: string;
  symbols: string[];
}

const SAMPLE_DREAMS: DreamEntry[] = [
  {
    id: "1",
    title: "Flying over golden mountains",
    date: "2026-04-20",
    mood: "Radiant",
    lucidity: 4,
    description: "I found myself soaring above ancient mountains bathed in golden light. I felt completely free and at peace.",
    symbols: ["flying", "mountains", "gold", "freedom"],
  },
  {
    id: "2",
    title: "Meeting an ancient sage",
    date: "2026-04-18",
    mood: "Peaceful",
    lucidity: 3,
    description: "An old teacher appeared and shared wisdom about the nature of consciousness and dreams.",
    symbols: ["sage", "teacher", "wisdom", "light"],
  },
];

const MOOD_OPTIONS = ["Radiant ☀️", "Peaceful 😊", "Neutral 😐", "Heavy 😔", "Struggling 🌧️"];
const LUCIDITY_LABELS = ["", "Barely aware", "Slightly aware", "Moderately lucid", "Fully lucid", "Complete lucidity"];

export function DreamsPage() {
  const [dreams, setDreams] = useState<DreamEntry[]>(SAMPLE_DREAMS);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    title: "", date: new Date().toISOString().split("T")[0],
    mood: "", lucidity: 3, description: "", symbolsText: "",
  });

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newDream: DreamEntry = {
      id: String(Date.now()),
      title: form.title,
      date: form.date,
      mood: form.mood,
      lucidity: form.lucidity,
      description: form.description,
      symbols: form.symbolsText.split(",").map((s) => s.trim()).filter(Boolean),
    };
    setDreams((p) => [newDream, ...p]);
    setShowModal(false);
    setForm({ title: "", date: new Date().toISOString().split("T")[0], mood: "", lucidity: 3, description: "", symbolsText: "" });
  }

  return (
    <>
      <section className="page-hero page-hero--brand">
        <div className="container">
          <p className="eyebrow">Inner Exploration</p>
          <h1 className="display-2">Dream Journal</h1>
          <p className="hero__body" style={{ maxWidth: "540px", margin: "0 auto" }}>
            Record, explore, and understand your dream world. Dreams are doorways
            to deeper wisdom and self-awareness.
          </p>
          <div style={{ marginTop: "1.5rem" }}>
            <button className="btn btn--brand btn--lg" onClick={() => setShowModal(true)}>
              + New Dream Entry
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section section--tight" style={{ background: "var(--c-surface-alt)" }}>
        <div className="container">
          <div className="dreams-stats-row">
            {[
              { label: "Total Dreams", value: dreams.length },
              { label: "This Month", value: dreams.length },
              { label: "Avg Lucidity", value: dreams.length > 0 ? (dreams.reduce((s, d) => s + d.lucidity, 0) / dreams.length).toFixed(1) : "—" },
              { label: "Common Symbol", value: "Light" },
            ].map((s) => (
              <div key={s.label} className="dreams-stat-card">
                <div className="dreams-stat-card__value">{s.value}</div>
                <div className="dreams-stat-card__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dream list */}
      <section className="section">
        <div className="container container--reading">
          {dreams.length === 0 ? (
            <div className="dreams-empty">
              <span style={{ fontSize: "3rem" }}>🌙</span>
              <h3>No dreams recorded yet</h3>
              <p>Start logging your dreams to discover patterns and insights.</p>
              <button className="btn btn--brand" onClick={() => setShowModal(true)}>Record First Dream</button>
            </div>
          ) : (
            <div className="dreams-timeline">
              {dreams.map((d) => (
                <div key={d.id} className="dream-entry-card">
                  <div className="dream-entry-card__header">
                    <div>
                      <h3 className="dream-entry-card__title">{d.title}</h3>
                      <p className="dream-entry-card__meta">
                        {d.date} · {d.mood} · Lucidity: {LUCIDITY_LABELS[d.lucidity]} ({d.lucidity}/5)
                      </p>
                    </div>
                    <div className="dream-lucidity-badge">L{d.lucidity}</div>
                  </div>
                  <p className="dream-entry-card__desc">{d.description}</p>
                  {d.symbols.length > 0 && (
                    <div className="dream-symbols">
                      {d.symbols.map((s) => (
                        <span key={s} className="dream-symbol-tag">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-box__header">
              <h2>New Dream Entry</h2>
              <button className="modal-box__close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="dreams-form">
              <div className="contact-form__row">
                <div className="form-group">
                  <label className="form-label" htmlFor="dream-title">Title *</label>
                  <input id="dream-title" name="title" type="text" className="form-input"
                    required value={form.title} onChange={handleChange} placeholder="Dream title" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="dream-date">Date *</label>
                  <input id="dream-date" name="date" type="date" className="form-input"
                    required value={form.date} onChange={handleChange} />
                </div>
              </div>

              <div className="contact-form__row">
                <div className="form-group">
                  <label className="form-label" htmlFor="dream-mood">Mood</label>
                  <select id="dream-mood" name="mood" className="form-input" value={form.mood} onChange={handleChange}>
                    <option value="">Select mood…</option>
                    {MOOD_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="dream-lucidity">
                    Lucidity Level: {form.lucidity}/5
                  </label>
                  <input id="dream-lucidity" name="lucidity" type="range" min="1" max="5"
                    value={form.lucidity}
                    onChange={(e) => setForm((p) => ({ ...p, lucidity: Number(e.target.value) }))}
                    style={{ width: "100%", marginTop: "0.5rem" }} />
                  <p style={{ fontSize: "0.8rem", color: "var(--c-ink-75)" }}>
                    {LUCIDITY_LABELS[form.lucidity]}
                  </p>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="dream-desc">Description *</label>
                <textarea id="dream-desc" name="description" className="form-input" rows={5}
                  required value={form.description} onChange={handleChange}
                  placeholder="Describe your dream in as much detail as you can remember…"
                  style={{ resize: "vertical" }} />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="dream-symbols">
                  Symbols / Tags (comma-separated)
                </label>
                <input id="dream-symbols" name="symbolsText" type="text" className="form-input"
                  value={form.symbolsText} onChange={handleChange}
                  placeholder="e.g. water, light, flying, temple" />
              </div>

              <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn btn--outline" onClick={() => setShowModal(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--brand" style={{ flex: 2 }}>
                  Save Dream
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="section--cta">
        <div className="container" style={{ textAlign: "center" }}>
          <h2>Want to go deeper?</h2>
          <p className="text-muted">Join the Astral Healing retreat and learn to master your dream state.</p>
          <Link to="/events/astral-healing-retreat-2026" className="btn btn--brand btn--lg">
            Explore the Retreat
          </Link>
        </div>
      </section>
    </>
  );
}
